import React, { useCallback, useEffect, useRef, useState } from 'react';
import { rgba } from 'polished';
import styled from 'styled-components';
import { Icon, InfiniteScrollingObserver } from '~/components';
import { colors } from '~/styles';

const Box = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5em 0.875em;
  border: solid 1px ${({ $isOpen }) => ($isOpen ? colors.primary : colors.grey10)};
  border-radius: 0.3125rem;
  background: ${colors.white};
  font-size: 1rem;
  user-select: none;
`;

const Control = styled.div`
  position: relative;
  width: 100%;
  cursor: pointer;
  outline: none;

  &:focus > ${Box} {
    border-color: ${colors.primary};
  }
`;

const Text = styled.div`
  flex: 1;
  font-size: 1rem;
  color: ${colors.black};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Placeholder = styled.span`
  color: ${colors.grey40};
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.5rem;
  gap: 0.25rem;
  flex-shrink: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0 0.25rem;
  cursor: pointer;
  color: ${colors.primary};
  font-size: 0.875rem;
  height: auto;
  border-radius: 0;

  &:hover {
    color: ${colors.accent};
    background: none;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  min-width: 22rem;
  z-index: 110;
  background: ${colors.white};
  border-radius: 0.3125rem;
  box-shadow: 0 0.25rem 1rem -0.125em ${rgba(colors.black, 0.15)};
`;

const SearchArea = styled.div`
  padding: 0.5rem 0.875rem;
  background: ${colors.grey5};
  border-bottom: ${({ $hasResults }) => ($hasResults ? `solid 1px ${colors.grey10}` : 'none')};
`;

const SearchInput = styled.input`
  width: 100%;
  font-size: 0.875rem;
  border: solid 1px ${colors.grey10};
  border-radius: 0.3125rem;
  padding: 0.375rem 0.625rem;
  outline: none;
  background: ${colors.white};
  color: ${colors.black};
  height: auto;

  &:focus {
    border-color: ${colors.primary};
  }

  &::placeholder {
    color: ${colors.grey40};
  }
`;

const Results = styled.div`
  max-height: 14rem;
  overflow-y: auto;
`;

const Item = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  cursor: pointer;
  background: ${({ $focused }) => ($focused ? colors.grey5 : 'transparent')};

  &:hover {
    background: ${colors.grey5};
  }
`;

const Empty = styled.div`
  padding: 0.75rem;
  font-size: 0.8125rem;
  color: ${colors.grey40};
  text-align: center;
`;

const Loading = styled.div`
  padding: 0.75rem;
  text-align: center;
  color: ${colors.primary};
  font-size: 0.875rem;
`;

export default function EntitySearchFilter({
  placeholder,
  selected,
  onSelect,
  onClear,
  searchFn,
  renderItem,
  renderSelected,
  onOpen,
}) {
  const [text, setText] = useState('');
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const textRef = useRef('');

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadInitial = useCallback(async () => {
    if (onOpen) {
      setIsLoading(true);
      try {
        const result = await onOpen();
        setResults(result.items || []);
        setHasMore(result.hasMore || false);
      } finally {
        setIsLoading(false);
      }
    } else {
      setResults([]);
      setHasMore(false);
    }
  }, [onOpen]);

  const handleOpen = useCallback(async () => {
    setIsOpen(true);
    setText('');
    textRef.current = '';
    setSearched(false);
    setResults([]);
    setFocusedIndex(-1);
    loadInitial();
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [loadInitial]);

  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value;
      setText(value);
      textRef.current = value;

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!value.trim()) {
        loadInitial();
        setSearched(false);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const result = await searchFn(value.trim());
        setResults(result.items || []);
        setHasMore(result.hasMore || false);
        setSearched(true);
        setFocusedIndex(-1);
      }, 250);
    },
    [searchFn, loadInitial],
  );

  const loadMore = useCallback(async () => {
    const currentText = textRef.current.trim();
    const fn = currentText ? searchFn : onOpen;
    if (!fn) return;

    const result = await fn(currentText || undefined, results.length);
    setResults((prev) => [...prev, ...(result.items || [])]);
    setHasMore(result.hasMore || false);
  }, [searchFn, onOpen, results.length]);

  const handleSelect = useCallback(
    (item) => {
      onSelect(item);
      setIsOpen(false);
      setText('');
      textRef.current = '';
      setResults([]);
      setHasMore(false);
    },
    [onSelect],
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onClear();
    },
    [onClear],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOpen();
        } else if (e.key === 'Backspace' && selected) {
          e.preventDefault();
          onClear();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev < results.length - 1 ? prev + 1 : prev;
            const item = resultsRef.current?.children[next];
            if (item) item.scrollIntoView({ block: 'nearest' });
            return next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : 0;
            const item = resultsRef.current?.children[next];
            if (item) item.scrollIntoView({ block: 'nearest' });
            return next;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < results.length) {
            handleSelect(results[focusedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
        case 'Tab':
          setIsOpen(false);
          break;
      }
    },
    [isOpen, results, focusedIndex, handleOpen, handleSelect, selected, onClear],
  );

  return (
    <Control ref={containerRef} tabIndex={0} onClick={() => !isOpen && handleOpen()} onKeyDown={handleKeyDown}>
      <Box $isOpen={isOpen}>
        <Text>
          {selected ? (
            renderSelected ? (
              renderSelected(selected)
            ) : (
              selected.label
            )
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
        </Text>
        <Status>
          {selected && (
            <ClearButton tabIndex={-1} onMouseDown={(e) => e.stopPropagation()} onClick={handleClear}>
              <Icon icon="times" />
            </ClearButton>
          )}
          <Icon icon="angle-down" color={colors.grey20} />
        </Status>
      </Box>

      {isOpen && (
        <Dropdown onClick={(e) => e.stopPropagation()}>
          <SearchArea $hasResults={results.length > 0 || isLoading}>
            <SearchInput
              ref={inputRef}
              placeholder={`Search ${placeholder.toLowerCase()}...`}
              value={text}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
            />
          </SearchArea>
          <Results ref={resultsRef}>
            {isLoading && (
              <Loading>
                <Icon icon="spinner" spin />
              </Loading>
            )}
            {!isLoading &&
              results.map((item, i) => (
                <Item key={item.id} $focused={i === focusedIndex} onClick={() => handleSelect(item)}>
                  {renderItem ? renderItem(item) : item.label}
                </Item>
              ))}
            {!isLoading && hasMore && <InfiniteScrollingObserver key={results.length} onIntersecting={loadMore} />}
            {!isLoading && searched && results.length === 0 && <Empty>No results found.</Empty>}
          </Results>
        </Dropdown>
      )}
    </Control>
  );
}
